import { Seo } from '../components/academy/Seo';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Car, CheckCircle, ShieldCheck, Phone, Video } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookInspection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [vinLoading, setVinLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(
    searchParams.get('tier') === 'roadtrip' ? 'roadtrip' : 'certified'
  );
  const [addCarfax, setAddCarfax] = useState(false);
  const [addNegotiation, setAddNegotiation] = useState(false);
  const [addConsultation, setAddConsultation] = useState(false);
  const [addSameDay, setAddSameDay] = useState(false);
  const [samedayCheck, setSamedayCheck] = useState(null); // {loading, eligible, reasons, checks}
  const [travelQuote, setTravelQuote] = useState(null); // {loading, roundTripMinutes, zone, zoneLabel, travelFee, approvalRequired, message, computed}
  const [blockedDates, setBlockedDates] = useState({}); // {date: message} from admin settings
  const [samedayCutoffHour, setSamedayCutoffHour] = useState(11);
  const [dateSurcharges, setDateSurcharges] = useState({}); // {date: {amount, label}}
  const [formData, setFormData] = useState({
    customerName: user?.name || '',
    email: user?.email || '',
    phone: '',
    carMake: '',
    carModel: '',
    carYear: new Date().getFullYear(),
    vin: '',
    location: '',
    preferredDate: '',
    preferredTime: '',
    referralSource: ''
  });

  const CARFAX_PRICE = 59.99;
  const SAME_DAY_PRICE = 49;
  const CONSULTATION_PRICE = 29;
  const NEGOTIATION_PRICE = 49;

  // Load admin-managed scheduling settings (blocked dates + same-day cutoff)
  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((res) => {
        const bd = {};
        (res.data?.blockedDates || []).forEach((b) => {
          if (b && b.date) bd[b.date] = b.message || "We're completely booked on this date. Please call us at 321.300.3256 for our next available appointment.";
        });
        setBlockedDates(bd);
        if (typeof res.data?.samedayCutoffHour === 'number') setSamedayCutoffHour(res.data.samedayCutoffHour);
        const ds = {};
        (res.data?.dateSurcharges || []).forEach((d) => {
          if (d && d.date) ds[d.date] = { amount: Number(d.amount) || 0, label: d.label || 'Holiday Service Fee' };
        });
        setDateSurcharges(ds);
      })
      .catch(() => {});
  }, []);

  // Same-Day eligibility check (debounced) whenever location/date/time change
  useEffect(() => {
    const { location, preferredDate, preferredTime } = formData;
    if (!location || !preferredDate || !preferredTime) {
      setSamedayCheck(null);
      setAddSameDay(false);
      return;
    }
    setSamedayCheck((prev) => ({ ...(prev || {}), loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/sameday/check`, { location, preferredDate, preferredTime });
        setSamedayCheck({ loading: false, ...res.data });
        setAddSameDay(!!res.data.eligible);
      } catch (e) {
        setSamedayCheck({ loading: false, eligible: false, reasons: ['Unable to check Same-Day availability right now.'] });
        setAddSameDay(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [formData.location, formData.preferredDate, formData.preferredTime]);

  // Extended Travel Fee estimate (debounced) whenever the inspection location changes
  useEffect(() => {
    const location = formData.location;
    if (!location || location.trim().length < 4) {
      setTravelQuote(null);
      return;
    }
    setTravelQuote((prev) => ({ ...(prev || {}), loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/travel/quote`, { location });
        setTravelQuote({ loading: false, ...res.data });
      } catch (e) {
        setTravelQuote(null);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [formData.location]);

  const travelFee = travelQuote && !travelQuote.loading && travelQuote.zone !== 'manual_review'
    ? (travelQuote.travelFee || 0) : 0;

  const holidaySurcharge = dateSurcharges[formData.preferredDate] || null;
  const holidayFee = holidaySurcharge ? holidaySurcharge.amount : 0;

  const tiers = {
    certified: {
      name: 'CarLens Certified Pre-Purchase Inspection',
      short: 'Certified Inspection',
      blurb: 'Cars, trucks & SUVs',
      promo: false,
      regular: 199,
      price: 199,
      features: [
        'Comprehensive 200+ point inspection',
        'Vehicle Decision Report™ with Confidence Score™',
        'CarLens Confidence Score™',
        'Detailed photo evidence & diagnostic scan',
        '24-Month Ownership Cost Forecast',
        'CarLens Vehicle Certification'
      ],
      note: 'Price includes the inspection & written report only. Live report review & consultation with your inspector is an additional $29.'
    },
    exotic: {
      name: 'CarLens Exotic Inspection',
      short: 'Exotic Inspection',
      blurb: 'Exotic, luxury & high-performance vehicles',
      promo: false,
      regular: 299,
      price: 299,
      features: [
        'Everything in the Certified inspection',
        'Specialist exotic & performance evaluation',
        'Cosmetic & paint-condition deep check',
        'Enhanced photo documentation',
        'Priority scheduling',
        'CarLens Vehicle Certification'
      ]
    },
    roadtrip: {
      name: 'CarLens Road Trip Ready Inspection',
      short: 'Road Trip Ready Inspection',
      blurb: 'Pre-trip safety check for any vehicle',
      promo: false,
      regular: 99,
      price: 99,
      features: [
        'Battery, brakes, tires & tread inspection',
        'Fluids, belts & hoses inspection',
        'OBD-II diagnostic scan',
        'Suspension, lights & wiper check',
        'Digital Vehicle Health Report',
        'Road Trip Readiness Score'
      ]
    }
  };

  const isRoadTrip = selectedTier === 'roadtrip';

  // Road Trip Ready is a flat $99 safety check — clear pre-purchase add-ons if selected.
  useEffect(() => {
    if (isRoadTrip) {
      setAddCarfax(false);
      setAddNegotiation(false);
      setAddConsultation(false);
      setAddSameDay(false);
    }
  }, [isRoadTrip]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const decodeVIN = async (vin) => {
    if (vin.length !== 17) {
      return;
    }

    setVinLoading(true);
    try {
      // Use NHTSA VIN Decoder API (free, no API key required)
      const response = await axios.get(
        `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`
      );

      if (response.data && response.data.Results) {
        const results = response.data.Results;
        
        // Extract relevant data
        const make = results.find(r => r.Variable === 'Make')?.Value || '';
        const model = results.find(r => r.Variable === 'Model')?.Value || '';
        const year = results.find(r => r.Variable === 'Model Year')?.Value || '';

        if (make && make !== 'Not Applicable') {
          setFormData(prev => ({
            ...prev,
            carMake: make,
            carModel: model && model !== 'Not Applicable' ? model : prev.carModel,
            carYear: year && year !== 'Not Applicable' ? parseInt(year) : prev.carYear
          }));
          toast.success('VIN decoded successfully!');
        } else {
          toast.error('Unable to decode VIN. Please enter vehicle details manually.');
        }
      }
    } catch (error) {
      console.error('VIN decode error:', error);
      toast.error('Failed to decode VIN. Please enter details manually.');
    } finally {
      setVinLoading(false);
    }
  };

  const handleVINChange = (e) => {
    const vin = e.target.value.toUpperCase();
    setFormData({ ...formData, vin });
    
    // Auto-decode when VIN is complete (17 characters)
    if (vin.length === 17) {
      decodeVIN(vin);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blockedDates[formData.preferredDate]) {
      toast.error('That date is fully booked', { description: blockedDates[formData.preferredDate] });
      return;
    }
    setLoading(true);

    try {
      // Guest booking request — no account, no payment yet. Owner reviews & approves,
      // then a secure payment link is emailed to the customer.
      const res = await axios.post(`${API}/bookings`, {
        ...formData,
        carYear: parseInt(formData.carYear),
        tier: selectedTier,
        addCarfax: addCarfax,
        addSameDay: addSameDay,
        addNegotiation: addNegotiation,
        addConsultation: addConsultation
      });

      setSubmittedId(res.data.id);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setLoading(false);
    } catch (error) {
      console.error('Error creating booking:', error);
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.reasons)) {
        toast.error(detail.message || 'This booking does not qualify for Same-Day service.', {
          description: detail.reasons.join(' '),
        });
      } else if (detail && typeof detail === 'object' && detail.message) {
        toast.error('Unable to book that date', { description: detail.message });
      } else {
        toast.error('Failed to submit your request. Please try again.');
      }
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <div className="max-w-lg w-full bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-10 text-center" data-testid="request-submitted-card">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Request Submitted!
          </h1>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Thanks! We've received your inspection request. Our team will verify the details and email you a
            <span className="text-white font-medium"> secure payment link</span>. Your appointment is confirmed once payment is received.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              onClick={() => navigate(`/status/${submittedId}`)}
              data-testid="submitted-track-btn"
            >
              Track My Request
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-white/5"
              onClick={() => navigate('/')}
              data-testid="submitted-home-btn"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <Seo siteName="CarLens" title="Book a Pre-Purchase Vehicle Inspection | CarLens" description="Book your independent CarLens pre-purchase vehicle inspection online. Mobile service across Northeast Florida from $199. Know the truth before you buy." path="/book" />
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button 
            variant="ghost" 
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <Car className="w-6 h-6 text-orange-500" />
            </div>
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Book Your Inspection
            </h1>
          </div>
          <p className="text-zinc-400 text-lg">Fill in the details below to schedule your car inspection</p>
        </div>

        {/* CarLens Remote Analysis — LIVE (top CTA) */}
        <button
          type="button"
          onClick={() => navigate('/firstlook')}
          className="w-full text-left relative overflow-hidden rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-zinc-950 p-6 mb-8 transition-all hover:border-emerald-400/70 hover:shadow-lg hover:shadow-emerald-500/10 group"
          data-testid="remote-analysis-cta"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Video className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">CarLens FirstLook™ · 1-on-1 Consultation</h3>
                <span className="text-emerald-400 font-bold">$49</span>
                <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border border-emerald-500/40">Available Now</span>
              </div>
              <p className="text-zinc-400 text-sm mb-3 max-w-xl">
                Not local, or not ready for an in-person inspection? Book a private 30-minute video consultation. Send us the listing, VIN, and photos, and a CarLens expert reviews the vehicle with you live — before you buy.
              </p>
              <ul className="text-sm text-zinc-300 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span> 30-minute private video consultation</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span> Expert review of listing, history &amp; price</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span> Virtual · available nationwide</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">✓</span> <span className="font-semibold text-white">$49 credited toward a local inspection</span></li>
              </ul>
            </div>
            <div className="shrink-0">
              <span className="inline-flex items-center gap-2 bg-emerald-500 group-hover:bg-emerald-400 text-white font-semibold px-6 py-3 rounded-full transition-colors" data-testid="remote-analysis-start-btn">
                Book FirstLook — $49 <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </button>

        {/* Choose Inspection Tier */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-white mb-6">Choose Your Inspection</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(tiers).map(([key, tier]) => {
              const selected = selectedTier === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setSelectedTier(key)}
                  className={`text-left relative bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border-2 p-6 transition-all ${selected ? 'border-orange-500 shadow-lg shadow-orange-500/20' : 'border-zinc-800 hover:border-zinc-600'}`}
                  data-testid={`tier-${key}`}
                >
                  {selected && (
                    <span className="absolute top-4 right-4 text-orange-400">
                      <CheckCircle className="w-6 h-6" />
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                  <p className="text-xs text-zinc-500 mb-3">{tier.blurb}</p>
                  <div className="mb-4">
                    {tier.promo && (
                      <span className="text-2xl font-semibold text-zinc-500 line-through mr-2">${tier.regular}</span>
                    )}
                    <span className="text-4xl font-bold text-orange-500">${tier.price}</span>
                    {tier.promo && <div className="text-xs font-semibold text-orange-300 mt-1">☀️ Summer Special</div>}
                  </div>
                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-zinc-300 text-sm">
                        <span className="text-orange-500 mt-0.5">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {tier.note && (
                    <p className="mt-4 text-xs text-zinc-400 leading-relaxed border-t border-zinc-800 pt-3" data-testid={`tier-${key}-note`}>{tier.note}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Customer Information */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="customerName" className="text-zinc-300">Full Name *</Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-zinc-300">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Vehicle Information</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="carMake" className="text-zinc-300">Make *</Label>
                  <Input
                    id="carMake"
                    name="carMake"
                    placeholder="e.g., Toyota, Honda, Ford"
                    value={formData.carMake}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="car-make-input"
                  />
                </div>
                <div>
                  <Label htmlFor="carModel" className="text-zinc-300">Model *</Label>
                  <Input
                    id="carModel"
                    name="carModel"
                    placeholder="e.g., Camry, Accord, F-150"
                    value={formData.carModel}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="car-model-input"
                  />
                </div>
                <div>
                  <Label htmlFor="carYear" className="text-zinc-300">Year *</Label>
                  <Input
                    id="carYear"
                    name="carYear"
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={formData.carYear}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="car-year-input"
                  />
                </div>
                <div>
                  <Label htmlFor="vin" className="text-zinc-300">VIN Number * (Auto-decodes vehicle info)</Label>
                  <Input
                    id="vin"
                    name="vin"
                    placeholder="Enter 17-character VIN"
                    value={formData.vin}
                    onChange={handleVINChange}
                    required
                    maxLength="17"
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white uppercase"
                    data-testid="vin-input"
                  />
                  {vinLoading && (
                    <p className="text-xs text-orange-400 mt-1 flex items-center gap-2">
                      <span className="animate-spin">⟳</span> Decoding VIN...
                    </p>
                  )}
                  {formData.vin.length > 0 && formData.vin.length < 17 && (
                    <p className="text-xs text-zinc-500 mt-1">{17 - formData.vin.length} characters remaining</p>
                  )}
                </div>
              </div>
            </div>

            {/* Inspection Details */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Inspection Details</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="location" className="text-zinc-300">Inspection Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="Full address or meeting location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="location-input"
                  />
                  {/* Live travel-fee estimate as the customer types their address */}
                  {formData.location && formData.location.trim().length >= 4 && travelQuote && (
                    <div className="mt-2 text-sm" data-testid="location-travel-estimate">
                      {travelQuote.loading ? (
                        <p className="text-zinc-400 flex items-center gap-2"><span className="animate-spin">⟳</span> Estimating travel fee for this address…</p>
                      ) : travelQuote.zone === 'standard' ? (
                        <p className="text-green-400">✓ Within our standard service area — no travel fee.</p>
                      ) : (travelQuote.zone === 'extended_service' || travelQuote.zone === 'extended_travel') ? (
                        <p className="text-orange-300">🚗 Estimated travel fee for this address: <span className="font-semibold">${(travelQuote.travelFee || 0).toFixed(2)}</span> — added to your total below.</p>
                      ) : (
                        <p className="text-orange-300">🚗 This address is outside our standard area. We'll confirm any travel fee with you before sending your payment link — you're never charged a surprise amount.</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="preferredDate" className="text-zinc-300">Preferred Date *</Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="preferred-date-input"
                  />
                  {blockedDates[formData.preferredDate] && (
                    <p className="mt-2 text-sm text-orange-300 font-medium" data-testid="blocked-date-msg">
                      {blockedDates[formData.preferredDate]}
                    </p>
                  )}
                  {!blockedDates[formData.preferredDate] && holidaySurcharge && (
                    <p className="mt-2 text-sm text-orange-300 font-medium" data-testid="holiday-fee-note">
                      {holidaySurcharge.label} of ${holidaySurcharge.amount.toFixed(2)} applies to appointments on this date (added to your total).
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="preferredTime" className="text-zinc-300">Preferred Time *</Label>
                  <Input
                    id="preferredTime"
                    name="preferredTime"
                    type="time"
                    value={formData.preferredTime}
                    onChange={handleChange}
                    required
                    className="mt-2 bg-zinc-950 border-zinc-800 text-white"
                    data-testid="preferred-time-input"
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="referralSource" className="text-zinc-300">How did you hear about us? *</Label>
                <select
                  id="referralSource"
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleChange}
                  required
                  className="mt-2 w-full rounded-md bg-zinc-950 border border-zinc-800 text-white px-3 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="referral-source-select"
                >
                  <option value="" disabled>Select an option</option>
                  <option value="Google">Google</option>
                  <option value="Yelp">Yelp</option>
                  <option value="ChatGPT or AI">ChatGPT or AI</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Other">Other</option>
                </select>
              </div>

            </div>

            {/* Add-ons Grid — pre-purchase only (hidden for Road Trip Ready) */}
            {!isRoadTrip && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Carfax Add-on */}
              <div className="bg-zinc-600/10 border border-zinc-600/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    id="addCarfax"
                    checked={addCarfax}
                    onChange={(e) => setAddCarfax(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-zinc-700 text-zinc-600 focus:ring-zinc-600"
                    data-testid="carfax-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="addCarfax" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">Carfax Report</h3>
                        <span className="bg-zinc-600 text-white text-xs font-bold px-2 py-1 rounded">+$59.99</span>
                      </div>
                      <ul className="text-sm text-zinc-300 space-y-1">
                        <li>✓ Complete accident history</li>
                        <li>✓ Service records</li>
                        <li>✓ Value estimates</li>
                        <li>✓ Title information</li>
                      </ul>
                    </label>
                  </div>
                </div>
              </div>

              {/* Negotiation Guidance Add-on */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    id="addNegotiation"
                    checked={addNegotiation}
                    onChange={(e) => setAddNegotiation(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-zinc-700 text-emerald-500 focus:ring-emerald-500"
                    data-testid="negotiation-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="addNegotiation" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">Negotiation Guidance</h3>
                        <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded">+$49</span>
                      </div>
                      <ul className="text-sm text-zinc-300 space-y-1">
                        <li>✓ Clear fair-market value from your report</li>
                        <li>✓ Understand what the vehicle is truly worth</li>
                        <li>✓ Confidence to make a fair, informed offer</li>
                      </ul>
                    </label>
                  </div>
                </div>
              </div>

              {/* Live Report Review & Inspector Consultation Add-on */}
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    id="addConsultation"
                    checked={addConsultation}
                    onChange={(e) => setAddConsultation(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-zinc-700 text-sky-500 focus:ring-sky-500"
                    data-testid="consultation-checkbox"
                  />
                  <div className="flex-1">
                    <label htmlFor="addConsultation" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">Live Report Review &amp; Inspector Consultation</h3>
                        <span className="bg-sky-500 text-white text-xs font-bold px-2 py-1 rounded">+${CONSULTATION_PRICE}</span>
                      </div>
                      <ul className="text-sm text-zinc-300 space-y-1">
                        <li>✓ Walk through your report live with your inspector</li>
                        <li>✓ Ask questions about any findings</li>
                        <li>✓ Clear guidance on next steps before you buy</li>
                      </ul>
                    </label>
                  </div>
                </div>
              </div>

              {/* Priority Same-Day Service — auto-applied when the appointment is booked for today & eligible */}
              <div className={`rounded-xl p-6 border transition-colors ${samedayCheck?.eligible ? 'bg-orange-500/10 border-orange-500/30' : 'bg-zinc-800/40 border-zinc-700/50'}`} data-testid="sameday-card">
                <div className="flex items-start gap-4">
                  <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${samedayCheck?.eligible ? 'bg-orange-500' : 'bg-zinc-700'}`}>
                    <CheckCircle className={`w-4 h-4 ${samedayCheck?.eligible ? 'text-white' : 'text-zinc-500'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white">Priority Same-Day Service</h3>
                      <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">+${SAME_DAY_PRICE.toFixed(2)}</span>
                      {samedayCheck?.eligible && <span className="text-[10px] font-bold text-orange-300 uppercase tracking-wide">Auto-added</span>}
                    </div>
                    <ul className="text-sm text-zinc-300 space-y-1">
                      <li>✓ <span className="font-semibold">Expedited inspection</span></li>
                      <li>✓ Same-day completion by 7:00 PM</li>
                      <li>✓ Priority scheduling</li>
                    </ul>
                    <p className="text-xs text-zinc-400 mt-2">Automatically added when you book an appointment for <span className="text-orange-300 font-medium">today</span>.</p>

                    {/* Eligibility status */}
                    <div className="mt-3 text-xs" data-testid="sameday-eligibility">
                      {(!formData.location || !formData.preferredDate || !formData.preferredTime) && (
                        <p className="text-zinc-400">Enter your location, date &amp; time above. Same-Day applies to appointments booked for <span className="text-orange-300">today</span>, placed before <span className="text-orange-300">{(samedayCutoffHour % 12) || 12}:00 {samedayCutoffHour < 12 ? 'AM' : 'PM'} ET</span>, within <span className="text-orange-300">20 miles</span>, starting by <span className="text-orange-300">5:00 PM</span>.</p>
                      )}
                      {samedayCheck?.loading && <p className="text-zinc-400">Checking Same-Day availability…</p>}
                      {samedayCheck && !samedayCheck.loading && samedayCheck.eligible && (
                        <p className="text-green-400 font-medium" data-testid="sameday-eligible-msg">✓ Priority Same-Day Service added (+${SAME_DAY_PRICE.toFixed(2)}){typeof samedayCheck.checks?.distance_miles === 'number' ? ` — about ${samedayCheck.checks.distance_miles} mi away` : ''}.</p>
                      )}
                      {samedayCheck && !samedayCheck.loading && !samedayCheck.eligible && samedayCheck.checks?.is_today && samedayCheck.reasons?.length > 0 && (
                        <ul className="text-orange-300 space-y-1" data-testid="sameday-ineligible-msg">
                          <li className="font-medium text-zinc-300">Same-Day service isn't available for this booking:</li>
                          {samedayCheck.reasons.map((r, i) => (<li key={i}>• {r}</li>))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Pricing Summary */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-zinc-300">{tiers[selectedTier].short}{tiers[selectedTier].promo && <span className="text-xs text-orange-300"> (Summer Special)</span>}</p>
                  <p className="text-white font-semibold">
                    {tiers[selectedTier].promo && (
                      <span className="text-zinc-500 line-through mr-2 text-sm">${tiers[selectedTier].regular.toFixed(2)}</span>
                    )}
                    ${tiers[selectedTier].price.toFixed(2)}
                  </p>
                </div>
                {addCarfax && (
                  <div className="flex justify-between items-center">
                    <p className="text-zinc-300">Carfax Vehicle History Report</p>
                    <p className="text-white font-semibold">${CARFAX_PRICE.toFixed(2)}</p>
                  </div>
                )}
                {addNegotiation && (
                  <div className="flex justify-between items-center">
                    <p className="text-zinc-300">Negotiation Guidance</p>
                    <p className="text-white font-semibold">${NEGOTIATION_PRICE.toFixed(2)}</p>
                  </div>
                )}
                {addConsultation && (
                  <div className="flex justify-between items-center" data-testid="consultation-fee-line">
                    <p className="text-zinc-300">Live Report Review &amp; Consultation</p>
                    <p className="text-white font-semibold">${CONSULTATION_PRICE.toFixed(2)}</p>
                  </div>
                )}
                {addSameDay && (
                  <div className="flex justify-between items-center" data-testid="sameday-fee-line">
                    <p className="text-zinc-300">Priority Same-Day Service</p>
                    <p className="text-white font-semibold">${SAME_DAY_PRICE.toFixed(2)}</p>
                  </div>
                )}
                {holidayFee > 0 && (
                  <div className="flex justify-between items-center" data-testid="holiday-fee-line">
                    <p className="text-zinc-300">{holidaySurcharge.label}</p>
                    <p className="text-white font-semibold">${holidayFee.toFixed(2)}</p>
                  </div>
                )}
                {travelFee > 0 && (
                  <div className="flex justify-between items-center" data-testid="travel-fee-line">
                    <p className="text-zinc-300">{travelQuote.zoneLabel || 'Extended Service Area'}</p>
                    <p className="text-white font-semibold">${travelFee.toFixed(2)}</p>
                  </div>
                )}
                <div className="border-t border-zinc-700 pt-3 flex justify-between items-center">
                  <p className="text-lg font-bold text-white">Total Amount</p>
                  <p className="text-2xl font-bold text-orange-500" data-testid="booking-total">
                    ${(tiers[selectedTier].price + (addCarfax ? CARFAX_PRICE : 0) + (addNegotiation ? NEGOTIATION_PRICE : 0) + (addConsultation ? CONSULTATION_PRICE : 0) + (addSameDay ? SAME_DAY_PRICE : 0) + holidayFee + travelFee).toFixed(2)}
                  </p>
                </div>
                {travelQuote && !travelQuote.loading && (travelQuote.zone === 'extended_service' || travelQuote.zone === 'extended_travel' || travelQuote.zone === 'manual_review') && (
                  <p className="text-xs text-orange-300/90 pt-1 leading-relaxed" data-testid="travel-fee-notice">{travelQuote.message}</p>
                )}
              </div>
            </div>

            {/* CarLens Membership - Coming Soon */}
            <div className="relative overflow-hidden rounded-xl border border-orange-500/30 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6" data-testid="membership-coming-soon">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-300 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border border-orange-500/40">Coming Soon</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-orange-400" />
                <h3 className="text-lg font-bold text-white">CarLens Membership</h3>
              </div>
              <p className="text-zinc-400 text-sm mb-5 max-w-xl">
                Unlimited access to CarLens guidance, member-only inspection pricing, and ongoing ownership support for every vehicle you own.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[140px] rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Monthly</p>
                  <p className="text-2xl font-bold text-white">$9.99<span className="text-sm text-zinc-500 font-normal">/mo</span></p>
                </div>
                <div className="flex-1 min-w-[140px] rounded-lg border border-orange-500/40 bg-orange-500/10 p-4 relative">
                  <span className="absolute -top-2 right-3 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">SAVE 33%</span>
                  <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">Yearly</p>
                  <p className="text-2xl font-bold text-white">$79.99<span className="text-sm text-zinc-500 font-normal">/yr</span></p>
                  <p className="text-xs text-green-400 mt-1">Just $6.67/mo — save ~$40</p>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-4">Membership launches soon — book your inspection today and we'll let you know when it's available.</p>
            </div>

            <Button
              type="submit"
              disabled={loading || !!blockedDates[formData.preferredDate]}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-5 h-auto min-h-[3.5rem] text-base sm:text-lg font-semibold rounded-lg !whitespace-normal leading-snug text-center px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-booking-btn"
            >
              {loading ? 'Submitting…' : (blockedDates[formData.preferredDate] ? 'Selected Date Unavailable' : 'Confirm Booking')}
            </Button>

            <p className="text-center text-sm text-zinc-500">
              No account or payment now. We'll review your request and email you a secure payment link showing your booking details. Your appointment is confirmed once payment is received.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookInspection;